import { Lock } from '@/services/get-ddocs-by-portal-address';
import {
  IDdoc,
  MigrationStatus,
  PortalCredentials,
  PortalServerKeys,
  PublishStatus,
} from '@/types';
import { IDB_NAME } from '@/utils/constants';
import { IRSAKeyPair } from '@/utils/identity-utils/types';
import { IComment } from '@fileverse-dev/ddoc/types';
import Dexie, { Table } from 'dexie';
import { Hex } from 'viem';

// Define your data structure

export interface PortalCollection {
  owner: Hex;
  portalAddress: Hex;
  credentials: PortalCredentials;
  serverKeys: PortalServerKeys;
}

export interface CommentSessionCollection {
  username: string;
  password: string;
}

export interface SessionCollection {
  address: Hex;
  signedMessage: Hex;
  expiresAt: number;
}

export interface CommentCacheItem {
  ddocId: string;
  commentId: string;
  author: string;
  isEnsLinked: boolean;
  message: string;
  createdAt: number;
  highlightedTextContent: string;
  lastReadTimestamp: number | null;
}

export interface UserCache {
  address: string;
  ddocId: string;
  createdAt: number;
  last_updated: number;
  publishStatus: PublishStatus;
  portalAddress: string;
  title: string | null | undefined;
  onChainFileId: number | null;
  linkKey: string | null;
  hasLinkKeyChanged?: boolean;
  linkKeyNonce: string | null;
  encryptedTitle?: string | null;
  archVersion?: string | null;
  ownerLock: Lock | null | undefined;
  portalLock: Lock | null | undefined;
  needToDecryptTitle: boolean;
  migrationStatus?: MigrationStatus | null;
  pendingTxnHash?: Hex | null;
  commentKey: string;
  markedForDeletion: boolean;
}

export interface UserIdentity {
  walletAddress: Hex;
  signedMessage: Hex;
  factoryAddress: Hex;
  identityModuleAddress: Hex | null;
}
export interface RepliesV2CacheItem extends IComment {
  txHash: string;
}

export interface CommentsV2CacheItem extends IComment {
  ddocId: string;
  lastReadTimestamp: number | null;
  contentHash: string;
  highlightYjsDiff: string;
  txHash: string;
  replies: RepliesV2CacheItem[];
}

export interface KeyStoreCache {
  portalAddress: Hex;
  hash: string;
  ownerKeyPair: IRSAKeyPair;
  memberKeyPair: IRSAKeyPair;
  portalKeyPair: IRSAKeyPair;
  editSecret: string;
  viewSecret: string;
  gunPassword: Hex;
  isLegacyUser: boolean;
}

export interface IdentityModuleCache {
  moduleAddress: Hex;
  accountPublicKey: Hex;
  accountPrivateKey: Hex;
  salt: Uint8Array;
  ag2Hash: string;
  signingDid: string;
  agentAddress: Hex;
}

// Create the database
export class DdocDatabase extends Dexie {
  ddocs!: Table<IDdoc, string>;
  portals!: Table<PortalCollection, Hex>;
  sessions!: Table<SessionCollection, Hex>;
  userCache!: Table<UserCache, string>;
  commentCache!: Table<CommentCacheItem, string>;
  userIdentity!: Table<UserIdentity, Hex>;
  commentsV2Cache!: Table<CommentsV2CacheItem, string>;
  commentAccount!: Table<CommentSessionCollection, string>;
  keyStoreCache!: Table<KeyStoreCache, string>;
  identityModuleCache!: Table<IdentityModuleCache, Hex>;
  constructor() {
    super(IDB_NAME);
    this.version(1).stores({
      ddocs: 'ddocId, createdAt, data',
      portals: 'owner',
      sessions: 'address, signedMessage, expiresAt',
      commentCache: '[ddocId+commentId]',
      userCache: '[address+ddocId]',
      userIdentity: '[walletAddress+factoryAddress]',
      commentsV2Cache: '[ddocId+id]',
      commentAccount: 'username',
      keyStoreCache: '[portalAddress+hash]',
      identityModuleCache: 'moduleAddress',
    });
  }
}

// Instantiate the database
export const db = new DdocDatabase();
